import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

/** Draw-to-sign canvas (mouse + touch) — captures the strokes as a JPEG File on submit. */
export function SignaturePad({ onCapture }: { onCapture: (file: File) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const ctx = () => canvasRef.current?.getContext("2d") ?? null;

  const pointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    const c = ctx();
    const { x, y } = pointerPos(e);
    if (c) {
      c.beginPath();
      c.moveTo(x, y);
    }
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const c = ctx();
    const { x, y } = pointerPos(e);
    if (c) {
      c.lineWidth = 2;
      c.lineCap = "round";
      c.strokeStyle = "#111827";
      c.lineTo(x, y);
      c.stroke();
    }
    setHasStrokes(true);
  };

  const stop = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const c = ctx();
    if (canvas && c) c.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  };

  const submit = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStrokes) return;
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onCapture(new File([blob], `signature-${Date.now()}.jpg`, { type: "image/jpeg" }));
        clear();
      },
      "image/jpeg",
      0.9,
    );
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={400}
        height={160}
        className="w-full touch-none rounded-lg border bg-white"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerLeave={stop}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear}>
          <Eraser className="mr-1.5 h-3.5 w-3.5" /> Clear
        </Button>
        <Button type="button" size="sm" disabled={!hasStrokes} onClick={submit}>
          Save signature
        </Button>
      </div>
    </div>
  );
}
